/**
 * Helper UI listing orphaned Xero Org IDs (invoices not linked to any org).
 *
 * Extracted from OrganisationManagementPage.tsx (Sub-PR 6.3).
 */

import { useTranslation } from 'react-i18next'
import type { OrphanedXeroOrgId } from './types'

const PREFIX_LEN = 8
const SUFFIX_LEN = 4
const AMOUNT_DECIMALS = 2

interface OrphanedXeroIdSelectorProps {
  orphans: OrphanedXeroOrgId[]
  selectedId: string
  onSelect: (id: string) => void
}

function truncateId(id: string): string {
  return `${id.slice(0, PREFIX_LEN)}...${id.slice(Math.max(0, id.length - SUFFIX_LEN))}`
}

export function OrphanedXeroIdSelector({
  orphans,
  selectedId,
  onSelect,
}: OrphanedXeroIdSelectorProps): React.ReactElement | null {
  const { t } = useTranslation('admin')
  if (orphans.length === 0) return null
  return (
    <div className="mb-2">
      <p className="mb-2 text-xs text-amber-600 dark:text-amber-400">
        {t('organisations.unlinkedInvoices', { count: orphans.length })}
      </p>
      <div className="max-h-32 space-y-1 overflow-y-auto">
        {orphans.map((orphan) => (
          <button
            key={orphan.id}
            type="button"
            onClick={() => {
              onSelect(orphan.id)
            }}
            className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${
              selectedId === orphan.id
                ? 'border border-[#4e5e82] bg-[#4e5e82]/10 dark:bg-[#8a9bba]/15'
                : 'border border-stone-200 bg-stone-50 hover:bg-stone-100 dark:border-stone-600 dark:bg-stone-700/50 dark:hover:bg-stone-700'
            }`}
          >
            <div className="flex items-center justify-between">
              <code className="truncate font-mono text-xs">{truncateId(orphan.id)}</code>
              <span className="ml-2 text-xs text-stone-500 dark:text-stone-400">
                {t('organisations.invoiceCount', { count: orphan.invoiceCount })} •{' '}
                {orphan.currency} {orphan.totalAmount.toFixed(AMOUNT_DECIMALS)}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}
